import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, MapPin, Clock, Phone, WhatsappLogo } from "@phosphor-icons/react";
import { Helmet } from "react-helmet-async";

const VisitePage = ({ config }) => {
  const { slug, title, h1, subtitle, heroImage, intro, distance, duration, highlights, sections, faq } = config;
  const whatsappMsg = encodeURIComponent(`Bonjour, je souhaite organiser une journée visite "${h1}" avec chauffeur privé. Pouvez-vous me faire un devis ?`);
  const whatsappUrl = `https://wa.me/33681969342?text=${whatsappMsg}`;

  const schema = {
    "@context": "https://schema.org",
    "@type": "TouristTrip",
    "name": h1,
    "description": intro.slice(0, 200),
    "touristType": "Luxury",
    "provider": { "@type": "LocalBusiness", "name": "ALTURA Conciergerie", "telephone": "+33681969342" },
    "url": `https://alturaconciergerie.com/destinations/${slug}`,
  };

  return (
    <main className="pt-20 bg-[#EDEBE4]" data-testid={`visite-${slug}`}>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={intro.slice(0, 155)} />
        <link rel="canonical" href={`https://alturaconciergerie.com/destinations/${slug}`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={intro.slice(0, 155)} />
        <meta property="og:url" content={`https://alturaconciergerie.com/destinations/${slug}`} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={heroImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">{JSON.stringify(schema)}</script>
      </Helmet>

      {/* Hero */}
      <section className="relative h-[55vh] min-h-[420px] overflow-hidden">
        <img src={heroImage} alt={h1} className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#EDEBE4] via-[#EDEBE4]/50 to-[#1A1A1A]/20" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-5xl mx-auto px-6 sm:px-12 pb-12 w-full">
            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
              <p className="text-xs tracking-[0.2em] uppercase text-[#1A1A1A]/70 mb-3">{subtitle}</p>
              <h1 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-normal text-[#1A1A1A] leading-tight">{h1}</h1>
              <div className="flex flex-wrap gap-6 mt-4">
                <span className="flex items-center gap-2 text-[#1A1A1A]/60 text-sm"><MapPin size={16} /> {distance}</span>
                <span className="flex items-center gap-2 text-[#1A1A1A]/60 text-sm"><Clock size={16} /> {duration}</span>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Intro + Contact CTA */}
      <section className="py-16 sm:py-20">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <div className="grid lg:grid-cols-3 gap-12">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="lg:col-span-2">
              <p className="text-[#1A1A1A] font-light leading-relaxed text-base mb-8">{intro}</p>
              <div className="flex flex-wrap gap-4">
                <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-8 py-3 text-sm tracking-wider uppercase hover:bg-[#20bd5a] transition-colors inline-flex items-center gap-2" data-testid="visite-cta-whatsapp">
                  <WhatsappLogo size={18} weight="fill" /> Devis par WhatsApp
                </a>
                <Link to="/contact" className="border border-[#1A1A1A] text-[#1A1A1A] px-8 py-3 text-sm tracking-wider uppercase hover:bg-[#1A1A1A] hover:text-[#EDEBE4] transition-colors inline-flex items-center gap-2" data-testid="visite-cta-contact">
                  <Phone size={16} /> Nous contacter
                </Link>
              </div>
            </motion.div>

            {/* Highlights card */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.2 }} className="bg-white border border-[#1A1A1A]/10 p-6">
              <h3 className="font-heading text-lg text-[#1A1A1A] mb-4">Points d'intérêt</h3>
              <ul className="space-y-3">
                {highlights.map((h, i) => (
                  <li key={i} className="flex items-start gap-3 text-[#1A1A1A]/80 text-sm">
                    <MapPin size={16} className="flex-shrink-0 mt-0.5 text-[#1A1A1A]/40" />
                    {h}
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Content sections */}
      {sections.map((section, i) => (
        <section key={i} className={`py-16 ${i % 2 === 0 ? "bg-white" : "bg-[#EDEBE4]"}`}>
          <div className="max-w-5xl mx-auto px-6 sm:px-12">
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
              <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A] mb-6">{section.title}</h2>
              <p className="text-[#1A1A1A]/80 font-light leading-relaxed max-w-3xl">{section.content}</p>
              {section.list && (
                <ul className="mt-6 space-y-3">
                  {section.list.map((item, j) => (
                    <li key={j} className="flex items-start gap-3 text-[#1A1A1A]/80 text-sm">
                      <span className="w-1.5 h-1.5 bg-[#1A1A1A] rounded-full mt-1.5 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              )}
            </motion.div>
          </div>
        </section>
      ))}

      {/* FAQ */}
      {faq && faq.length > 0 && (
        <section className="py-16 sm:py-20 bg-[#EDEBE4]">
          <div className="max-w-5xl mx-auto px-6 sm:px-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-normal text-[#1A1A1A] mb-10">Questions fréquentes</h2>
            <div className="space-y-6">
              {faq.map((item, i) => (
                <motion.div key={i} initial={{ opacity: 0, y: 10 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}>
                  <h3 className="text-[#1A1A1A] font-medium text-sm mb-2">{item.q}</h3>
                  <p className="text-[#1A1A1A]/70 text-sm font-light leading-relaxed">{item.a}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Bottom CTA */}
      <section className="py-16 bg-[#1A1A1A]">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl text-[#EDEBE4] mb-4">Organisez votre journée</h2>
          <p className="text-[#EDEBE4]/60 text-sm mb-8">Chaque visite est personnalisée selon vos envies. Contactez-nous pour un devis sur mesure.</p>
          <div className="flex flex-wrap justify-center gap-4">
            <a href={whatsappUrl} target="_blank" rel="noopener noreferrer" className="bg-[#25D366] text-white px-10 py-4 text-sm tracking-wider uppercase hover:bg-[#20bd5a] transition-colors inline-flex items-center gap-2" data-testid="visite-cta-bottom-wa">
              <WhatsappLogo size={18} weight="fill" /> WhatsApp
            </a>
            <a href="tel:+33681969342" className="bg-[#EDEBE4] text-[#1A1A1A] px-10 py-4 text-sm tracking-wider uppercase hover:bg-white transition-colors inline-flex items-center gap-2" data-testid="visite-cta-bottom-call">
              <Phone size={16} /> Appeler
            </a>
          </div>
        </div>
      </section>
    </main>
  );
};

export default VisitePage;
